// Site-wide JSON-LD helpers. Each composable injects a single
// <script type="application/ld+json"> block into <head>. Call from
// pages or layouts as needed. The same key string is used as a head
// `key` so re-renders replace the block instead of stacking.

type LdRecord = Record<string, unknown>;

const useSchemaOrgBase = () => {
  const config = useRuntimeConfig();
  const siteBase = (config.public.appUrl as string | undefined)?.replace(/\/$/, '')
    || 'https://insidersofdubai.com';
  return { siteBase };
};

const injectLd = (key: string, json: LdRecord | LdRecord[] | null) => {
  useHead({
    script: computed(() => {
      if (!json) return [];
      return [{
        key,
        type: 'application/ld+json',
        innerHTML: JSON.stringify(json),
      }];
    }),
  });
};

// Site-wide Organization block. Place ONCE in the default layout so every
// indexable page emits it. Drives the knowledge-panel logo + sameAs links.
export const useOrganizationSchema = () => {
  const { siteBase } = useSchemaOrgBase();
  const ld: LdRecord = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteBase}/#organization`,
    name: 'Insiders of Dubai',
    url: siteBase,
    logo: `${siteBase}/images/logos/mark-gold.png`,
    image: `${siteBase}/og-image.png`,
    description:
      'Dubai\'s lucrative off-plan property platform — exclusive early access, '
      + 'developer briefings, and verified investor deals.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@insidersofdubai.com',
      areaServed: 'AE',
      availableLanguage: ['en', 'ar', 'ru'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Dubai',
      addressCountry: 'AE',
    },
  };
  injectLd('schema-org-organization', ld);
};

// WebSite + SearchAction (sitelinks search box in Google SERP). Place ONCE
// in the default layout alongside Organization.
export const useWebsiteSchema = () => {
  const { siteBase } = useSchemaOrgBase();
  const ld: LdRecord = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteBase}/#website`,
    url: siteBase,
    name: 'Insiders of Dubai',
    publisher: { '@id': `${siteBase}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteBase}/explore?query={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'en',
  };
  injectLd('schema-org-website', ld);
};

// Breadcrumb list. Call per-page with the trail of crumbs.
export const useBreadcrumbSchema = (
  crumbs: MaybeRefOrGetter<Array<{ name: string; url: string }>>,
) => {
  const { siteBase } = useSchemaOrgBase();
  const ld = computed<LdRecord | null>(() => {
    const list = toValue(crumbs);
    if (!Array.isArray(list) || list.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: list.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        item: c.url.startsWith('http') ? c.url : `${siteBase}${c.url}`,
      })),
    };
  });
  useHead({
    script: computed(() => {
      if (!ld.value) return [];
      return [{
        key: 'schema-org-breadcrumb',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(ld.value),
      }];
    }),
  });
};

// Canonical link tag. Pass the path (e.g. '/developers/emaar') and the
// composable prepends the configured site base.
export const useCanonical = (path: MaybeRefOrGetter<string | null | undefined>) => {
  const { siteBase } = useSchemaOrgBase();
  useHead({
    link: computed(() => {
      const p = toValue(path);
      if (!p) return [];
      const clean = p.startsWith('http') ? p : `${siteBase}${p.startsWith('/') ? p : `/${p}`}`;
      return [{ rel: 'canonical', href: clean }];
    }),
  });
};
